import React, { useEffect, useState, useRef } from "react";
import { fetchStripeProducts } from "@/utils/api/index";
import {
  CheckCircleIcon,
  CurrencyDollarIcon,
} from "@heroicons/react/24/outline"; // Added CurrencyDollarIcon

const tiers = ["SILVER", "GOLD", "PLATINUM"];
const PLAN_FEATURES = [
  [
    "Full-Color listing",
    "Link to your website",
    "Fly-Inn Find game Participation",
    "Fly-Inn Plaque",
    "Instagram frame",
    "Blog just about YOU that links to your website",
    "First-Class Perks Pass",
  ],
  [
    "Full-Color listing",
    "Link to your website",
    "Fly-Inn Find game Participation",
    "Fly-Inn Plaque",
    "Instagram frame",
    "Blog just about YOU that links to your website",
    "First-Class Perks Pass",
    "$50 gift certificate plus another $50 gift certificate if you film your stay!",
    "15-second video in our social media accounts!",
  ],
  [
    "Full-Color listing",
    "Link to your website",
    "Fly-Inn Find game Participation",
    "Fly-Inn Plaque",
    "Instagram frame",
    "Blog just about YOU that links to your website",
    "First-Class Perks Pass",
    "$50 gift certificate plus another $50 gift certificate if you film your stay!",
    "30-second video in our social media accounts!",
    "Feature your business, FREE for 30 days",
    "Podcast/interview",
  ],
];

// PLAN_PRICING is still used for the initial display,
// but actual Stripe prices are fetched dynamically.
const PLAN_PRICING = [
  { yearly: 120, monthly: 15 },
  { yearly: 180, monthly: 27 },
  { yearly: 240, monthly: 36 },
];

const UpdatePaymentPlan = ({
  setCurrentStep,
  billingCycle,
  setBillingCycle,
}) => {
  const [plans, setPlans] = useState([]);
  const [selected, setSelected] = useState(null); // No plan selected initially
  const [loadingStripePlans, setLoadingStripePlans] = useState(true);

  const billingRef = useRef(null); // Use useRef for the scroll target

  useEffect(() => {
    const fetchPlans = async () => {
      setLoadingStripePlans(true);
      try {
        const res = await fetchStripeProducts();
        setPlans(res || []);
      } catch (error) {
        console.error("Failed to fetch Stripe products:", error);
        setPlans([]); // Set to empty array on error
      } finally {
        setLoadingStripePlans(false);
      }
    };
    fetchPlans();
  }, []);

  const handleSelect = (idx) => {
    setSelected(idx);
    setBillingCycle({ freq: "", value: null, tiers: "" }); // Reset billing cycle when switching plans
    setTimeout(() => {
      if (billingRef.current) {
        billingRef.current.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }
    }, 50);
  };

  const renderBillingOptions = () => {
    if (selected === null) {
      return (
        <div className="text-center text-gray-600 p-8 bg-gray-50 rounded-xl mt-6 animate-fadeIn">
          <CurrencyDollarIcon className="w-12 h-12 mx-auto mb-4 text-red-400" />
          <p className="text-lg font-semibold">
            Select a plan above to see billing options.
          </p>
          <p className="text-sm text-gray-500">
            Click on any of the pricing cards to choose your membership tier.
          </p>
        </div>
      );
    }

    const selectedTierName = tiers[selected];

    const monthlyPlan = plans.find(
      (plan) =>
        plan.name.toUpperCase().includes(selectedTierName) &&
        plan.name.toUpperCase().includes("MONTHLY")
    );

    const yearlyPlan = plans.find(
      (plan) =>
        plan.name.toUpperCase().includes(selectedTierName) &&
        plan.name.toUpperCase().includes("YEARLY")
    );

    if (loadingStripePlans) {
      return (
        <div className="text-center text-gray-600 p-8 bg-gray-50 rounded-xl mt-6 animate-fadeIn">
          <div className="flex justify-center items-center h-20">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-500"></div>
          </div>
          <p className="text-lg font-semibold mt-4">
            Loading billing options for {selectedTierName}...
          </p>
        </div>
      );
    }

    if (!monthlyPlan || !yearlyPlan) {
      return (
        <div className="text-center text-red-700 p-8 bg-red-50 rounded-xl mt-6 animate-fadeIn">
          <p className="text-lg font-semibold">
            Billing details for {selectedTierName} not found. Please try again
            later.
          </p>
          <p className="text-sm text-red-600">
            There might be an issue fetching plan details from our payment
            provider.
          </p>
        </div>
      );
    }

    const monthlyPriceYearly = monthlyPlan.price * 12;
    const yearlyPrice = yearlyPlan.price;
    const savings = monthlyPriceYearly - yearlyPrice;
    const savingsPercentage =
      yearlyPrice > 0 ? ((savings / monthlyPriceYearly) * 100).toFixed(0) : 0;

    return (
      <div className="mt-8 animate-fadeIn space-y-6">
        <h3 className="text-2xl font-bold text-center text-gray-800">
          Choose Billing Cycle for{" "}
          <span className="text-red-600">{selectedTierName}</span> Plan
        </h3>

        <p className="text-center text-gray-600 text-sm md:text-base !mb-1">
          Select how you'd like to be billed.
        </p>

        <div className="flex flex-col md:flex-row justify-center gap-4">
          <button
            className={`px-6 py-3 border-solid rounded-md text-lg font-semibold border transition-all duration-150
              ${
                billingCycle?.freq === "yearly" &&
                billingCycle?.tiers === selectedTierName
                  ? "bg-red-600 text-white border-red-600 ring-2 ring-red-200"
                  : "bg-white text-red-700 border-red-300 hover:bg-red-50"
              }`}
            onClick={(e) => {
              e.stopPropagation();
              setBillingCycle({
                freq: "yearly",
                value: yearlyPlan,
                tiers: selectedTierName,
              });
            }}
          >
            Pay Yearly – ${yearlyPlan.price}/year
          </button>

          <button
            className={`px-6 py-3 border-solid rounded-md text-lg font-semibold border transition-all duration-150
              ${
                billingCycle?.freq === "monthly" &&
                billingCycle?.tiers === selectedTierName
                  ? "bg-red-600 text-white border-red-600 ring-2 ring-red-200"
                  : "bg-white text-red-700 border-red-300 hover:bg-red-50"
              }`}
            onClick={(e) => {
              e.stopPropagation();
              setBillingCycle({
                freq: "monthly",
                value: monthlyPlan,
                tiers: selectedTierName,
              });
            }}
          >
            Pay Monthly – ${monthlyPlan.price}/month
          </button>
        </div>

        {savings > 0 && (
          <p className="text-center text-sm text-green-700">
            Save {savingsPercentage}% by choosing yearly billing (${savings}{" "}
            off)
          </p>
        )}

        {billingCycle?.freq && billingCycle?.tiers === selectedTierName && (
          <div className="mt-4 text-center text-base font-medium text-gray-700">
            You selected the{" "}
            <span className="text-red-600 font-bold">{selectedTierName}</span>{" "}
            plan billed{" "}
            <span className="text-red-600 font-bold">{billingCycle.freq}</span>{" "}
            at{" "}
            <span className="text-red-600 font-bold">
              ${billingCycle.value.price}
              {billingCycle.freq === "yearly" ? "/year" : "/month"}
            </span>
            .
            <p className="text-sm text-gray-500 mt-1">
              Click 'Next' to proceed to payment.
            </p>
          </div>
        )}
      </div>
    );
  };

  // Main loading state for the entire component
  if (loadingStripePlans && plans.length === 0 && selected === null) {
    return (
      <div className="flex justify-center items-center min-h-[40vh]">
        <div className="flex flex-col items-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-red-500 mb-4"></div>
          <p className="text-lg text-gray-700">Fetching available plans...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center w-full pb-8 !pt-0">
      <div className="flex flex-col md:flex-row gap-6 w-full justify-center">
        {PLAN_PRICING.map((plan, idx) => (
          <div
            key={idx}
            className={`flex-1 border-solid   hover:border-red-500 hover:border-solid hover:border rounded-2xl shadow-lg border-1 transition-all duration-200 bg-white px-4 py-5 flex flex-col items-center cursor-pointer relative
              ${
                selected === idx
                  ? "border-red-600 ring-2 ring-red-200 shadow-2xl z-10"
                  : "border-gray-200 hover:border-red-300" // Added hover effect
              }`}
            onClick={() => handleSelect(idx)}
          >
            <div className="mb-5 w-full">
              <h3 className="text-2xl font-bold text-red-700 mb-2 text-center">
                {tiers[idx]} Plan
              </h3>{" "}
              {/* Changed "Plan X" to actual tier name */}
              <ul className="text-left space-y-2 !pl-0 mt-4">
                {PLAN_FEATURES[idx].map((feature, i) => (
                  <li key={i} className="flex items-start gap-2 text-gray-700">
                    <CheckCircleIcon className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div
              className={`mt-auto mb-[10px] rounded-[10px] flex flex-col items-center py-3 rounded-b-xl w-[calc(100%+2px)]  -mx-4 border-t border-gray-100 transition-all duration-200 cursor-pointer
                ${
                  selected === idx
                    ? "bg-red-600 !text-white"
                    : "bg-red-50 hover:bg-red-100 text-red-700"
                }`}
            >
              <span
                className={`text-3xl font-extrabold mb-1 ${
                  selected === idx ? "!text-white" : ""
                }`}
              >
                ${plan.yearly}
                <span
                  className={`text-lg font-normal ${
                    selected === idx ? " !text-white" : ""
                  }`}
                >
                  /year
                </span>
              </span>
              <span
                className={`text-base font-medium ${
                  selected === idx ? "!text-white" : ""
                }`}
              >
                or ${plan.monthly}/month
              </span>
            </div>
          </div>
        ))}
      </div>

      <div ref={billingRef} className="w-full mt-10">
        {renderBillingOptions()}
      </div>

      {/* Action Buttons */}
      <div className="mt-12 flex flex-col sm:flex-row gap-4 justify-between w-full ">
        <button
          className="w-full sm:w-auto px-8 py-3 bg-gray-100 text-gray-700 font-medium rounded-lg border border-gray-300 hover:bg-gray-200 transition-colors duration-200 shadow-sm"
          onClick={() => setCurrentStep(1)}
        >
          Back
        </button>
        <button
          className={`w-full sm:w-auto px-8 py-3 font-medium rounded-lg transition-all duration-200 shadow-md
            ${
              billingCycle?.value?.product_id // Check for actual Stripe price ID
                ? "bg-[#af2322] text-white hover:bg-[#9e1f1a] transform hover:scale-105" // Subtle hover effect
                : "bg-gray-200 text-gray-400 cursor-not-allowed"
            }`}
          onClick={() => billingCycle?.value?.product_id && setCurrentStep(2)} // Use value.id for Stripe price ID
          disabled={!billingCycle?.value?.product_id} // Disable if no Stripe price ID is selected
        >
          Next: Payment Summary
        </button>
      </div>
    </div>
  );
};

export default UpdatePaymentPlan;
